// import React from "react";
// import { Card, Col, Row } from "react-bootstrap";
// import "react-loading-skeleton/dist/skeleton.css";
// import { useNavigate } from "react-router-dom";

// const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";

// const EventGalleryList = ({ data, eventDetail = false }) => {
//   const navigate = useNavigate();
//   return (
//     <>
//       <Row className="my-4 py-2">
//         {data.length > 0 &&
//           data.map((item) => (
//             <Col
//               key={item._id}
//               lg={eventDetail ? 3 : 4}
//               md={eventDetail ? 4 : 6}
//               sm={12}
//               className="d-flex align-items-center justify-content-center"
//             >
//               <Card className="border-0 w-100 h-100 pb-4"
//                 onClick={() => {
//                   if (!eventDetail) {
//                     navigate(`/events-gallery/${item._id}`);
//                   }
//                 }} >
//                 <Card.Img
//                   className="event-img"
//                   src={`${IMAGE_BASEURL}${item?.cover_image_path || item?.image_path}`}
//                 />
//                 {!eventDetail && item?.title && <Card.Body className="pb-0">
//                   <Card.Title className="text-center fw-semibold text-main event-gallary-title">
//                     {item?.title}
//                   </Card.Title>
//                 </Card.Body>}
//               </Card>
//             </Col>
//           ))}
//       </Row>
//     </>
//   );
// };

// export default EventGalleryList;

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Col, Row, Modal } from "react-bootstrap";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";

const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";

const EventGalleryList = ({ data = [], eventDetail = false }) => {
  const navigate = useNavigate();

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // zoom / pan state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  // fullscreen state
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // clamp helpers
  const MIN_SCALE = 0.5;
  const MAX_SCALE = 4;
  const STEP = 0.25;

  const images = useMemo(
    () => data.map((it) => `${IMAGE_BASEURL}${it?.cover_image_path || it?.image_path}`),
    [data]
  );

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // keyboard handlers
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+") zoomIn();
      else if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, scale]);

  const openAt = (index) => {
    if (!images.length) return;
    setCurrentIndex(index);
    setShowModal(true);
    resetZoom();
  };

  const closeModal = () => {
    setShowModal(false);
    exitFullscreenIfNeeded();
  };

  const prev = () => {
    setCurrentIndex((i) => (images.length ? (i - 1 + images.length) % images.length : 0));
    resetZoom();
  };
  const next = () => {
    setCurrentIndex((i) => (images.length ? (i + 1) % images.length : 0));
    resetZoom();
  };

  const zoomIn = () =>
    setScale((s) => Math.min(MAX_SCALE, Math.round((s + STEP) * 100) / 100));
  const zoomOut = () =>
    setScale((s) => Math.max(MIN_SCALE, Math.round((s - STEP) * 100) / 100));
  const resetZoom = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  // Fullscreen
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      if (containerRef.current?.requestFullscreen) {
        try {
          await containerRef.current.requestFullscreen();
        } catch (err) {
          console.warn("Fullscreen request failed", err);
        }
      }
    } else {
      await exitFullscreenIfNeeded();
    }
  };

  const exitFullscreenIfNeeded = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (err) {
        console.warn("Exit fullscreen failed", err);
      }
    }
  };

  // Panning handlers (mouse + touch)
  const onPointerDown = (clientX, clientY) => {
    setIsPanning(true);
    lastPosRef.current = { x: clientX, y: clientY };
  };

  const onPointerMove = (clientX, clientY) => {
    if (!isPanning) return;
    const last = lastPosRef.current;
    const dx = clientX - last.x;
    const dy = clientY - last.y;
    lastPosRef.current = { x: clientX, y: clientY };
    setTranslate((t) => ({ x: t.x + dx, y: t.y + dy }));
  };

  const onPointerUp = () => setIsPanning(false);

  useEffect(() => {
    const onUp = () => setIsPanning(false);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  // image transform style (image constrained inside viewport)
  const imageTransformStyle = {
    transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
    transformOrigin: "center center",
    cursor: isPanning ? "grabbing" : scale > 1 ? "grab" : "default",
    transition: isPanning ? "none" : "transform 120ms ease",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    display: "block",
    userSelect: "none",
    touchAction: "none",
  };

  // viewport container that holds the image; fixed-size so modal doesn't resize
  const viewportStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    borderRadius: 6,
    background: "var(--text-main)",
    width: "100%",
    height: "100%", // fill fixed container
    position: "relative", // <-- allow absolutely positioned overlay buttons
  };

  // overlay button style
  const overlayBtnStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 20,
    background: "rgba(0,0,0,0.45)",
    border: "none",
    color: "#fff",
    width: 48,
    height: 48,
    borderRadius: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
    fontSize:"2rem"
  };

  return (
    <>
      <Row className="my-4 py-2">
        {data.length > 0 &&
          data.map((item, idx) => {
            const imgSrc = `${IMAGE_BASEURL}${item?.cover_image_path || item?.image_path}`;

            return (
              <Col
                key={item._id || idx}
                lg={eventDetail ? 3 : 4}
                md={eventDetail ? 4 : 6}
                sm={12}
                className="d-flex align-items-center justify-content-center"
              >
                <Card
                  className="border-0 w-100 h-100 pb-4"
                  onClick={() => {
                    if (eventDetail) openAt(idx);
                    else navigate(`/events-gallery/${item._id}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Card.Img className="event-img" src={imgSrc} alt={item?.title || idx} />
                  {!eventDetail && item?.title && (
                    <Card.Body className="pb-0">
                      <Card.Title className="text-center fw-semibold text-main event-gallary-title">
                        {item?.title}
                      </Card.Title>
                    </Card.Body>
                  )}
                </Card>
              </Col>
            );
          })}
      </Row>

      {/* Modal - fixed-size viewport container so modal won't resize with image */}
      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        backdrop="static"
        contentClassName="p-0"
        className="preview-modal bg-white"
      >
        <div
          ref={containerRef}
          style={{
            background: "var(--text-main)",
            color: "#fff",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "90vw",
            height: "90vh",
            boxSizing: "border-box",
          }}
        >
          {/* Responsive header: stacks on small screens, row on md+ */}
          <div className="d-flex flex-row align-items-center justify-content-between image-gallery-preview" style={{ gap: 8, padding: '12px 24px', background: "var(--text-1)" }}>
            {/* LEFT: zoom controls */}
             <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                aria-label="Toggle Fullscreen"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "none",
                  color: "#fff",
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                {isFullscreen ? "Exit Full" : "Fullscreen"}
              </button>
           

            {/* CENTER: prev / next / fullscreen — centered on md+, full width stacked on mobile */}
            <div className="d-flex align-items-center justify-content-center" style={{ flex: 1, gap: 8 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <div style={{ color: "#bbb", fontSize: 14 }}>
                {currentIndex + 1} / {images.length}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  zoomOut();
                }}
                aria-label="Zoom Out"
                className="btn"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "none",
                  color: "#fff",
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                −
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  resetZoom();
                }}
                aria-label="Reset Zoom"
                className="btn"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "none",
                  color: "#fff",
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                ⤾
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  zoomIn();
                }}
                aria-label="Zoom In"
                className="btn"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "none",
                  color: "#fff",
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                +
              </button>
            </div>
             
           
            </div>

            {/* RIGHT: index + close (aligned right on md+, stacked below on mobile) */}
            <div className="d-flex align-items-center justify-content-end" style={{ gap: 16 }}>

              <button
                onClick={closeModal}
                aria-label="Close"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: 22,
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* VIEWPORT: fixed-size area; image is constrained inside */}
          <div
            onMouseDown={(ev) => {
              if (scale <= 1) return;
              onPointerDown(ev.clientX, ev.clientY);
            }}
            onMouseMove={(ev) => {
              if (scale <= 1) return;
              onPointerMove(ev.clientX, ev.clientY);
            }}
            onMouseUp={() => onPointerUp()}
            onMouseLeave={() => onPointerUp()}
            onTouchStart={(ev) => {
              if (!ev.touches?.length) return;
              const t = ev.touches[0];
              if (scale <= 1) return;
              onPointerDown(t.clientX, t.clientY);
            }}
            onTouchMove={(ev) => {
              if (!ev.touches?.length) return;
              const t = ev.touches[0];
              if (scale <= 1) return;
              onPointerMove(t.clientX, t.clientY);
            }}
            onTouchEnd={() => onPointerUp()}
            style={{
              ...viewportStyle,
              width: "100%",
              height: "calc(100% - 88px)",
            }}
          >
            {/* Overlay prev/next buttons on the left/right of image */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prev();
                  }}
                  aria-label="Previous image"
                  style={{ ...overlayBtnStyle, left: 12 }}
                >
                  ‹
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    next();
                  }}
                  aria-label="Next image"
                  style={{ ...overlayBtnStyle, right: 12 }}
                >
                  ›
                </button>
              </>
            )}

            <img
              src={images[currentIndex]}
              alt={`preview-${currentIndex}`}
              style={imageTransformStyle}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
            />
          </div>

          {/* footer hint */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              color: "#fff",
              fontSize: 12,
              padding: '12px 24px'
            }}
          >
            <div>Zoom: {(scale * 100).toFixed(0)}% &nbsp;|&nbsp; Reset: ⤾</div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EventGalleryList;
